import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { store } from './redux/store';
import { fetchUserProfile } from './redux/slices/authSlice';
import { getDataCommon } from './redux/slices/dataCommonSlice';
import { setApiStore } from './services/api';
import { hasPermission } from './utils/permissions';
import { Result } from 'antd';
import './App.css';

// Components
import AppLoading from './components/AppLoading';
import TokenExpiredDialog from './components/TokenExpiredDialog';

// Layouts
import MainLayout from './layouts/MainLayout';
import AmzAdsLayout from './layouts/AmzAdsLayout';

// Pages
import Home from './pages/Home';
import OrderList from './modules/Orders';
import OrderDetail from './modules/OrderDetail';
import AdminSettings from './modules/AdminSettings';
import SkuItems from './modules/SkuItems';
import Listings from './modules/Listings';
import NicheAnalysis from './modules/NicheAnalysis';
import CategoryManagement from './modules/CategoryManagement';
import ShopifyCategoryManagement from './modules/ShopifyCategoryManagement';
import ProfileSettings from './modules/ProfileSettings';
import Inbox from './modules/Inbox';
import AdsOptimization from './modules/AdsOptimization';
import FlowBuilder from './modules/AdsOptimization/FlowBuilder';
import ShopifyProducts from './modules/shopify/ShopifyProducts';
import ShopifyVariants from './modules/shopify/ShopifyVariants';
import ShopifySettings from './modules/shopify/ShopifySettings';
import ShopifyOrders from './modules/ShopifyOrders';
import SystemSettings from './modules/SystemSettings';
import SystemCategories from './modules/SystemCategories';
import DesignScale from './modules/DesignScale';
import DesignerBonus from './modules/DesignerBonus';
import Login from './pages/Login';
import FinancialTransactions from './modules/FinancialTransactions';
import Specstudio from './modules/Specstudio';
import ProfitReport from './modules/AmzAds/ProfitReport';

// Protected route component with permission checking
const ProtectedRoute = ({ children, requiredPermission = null }) => {
  const { isAuthenticated, loading, user } = useSelector(state => state.auth);
  
  if (loading) {
    return <AppLoading message="Authenticating user..." />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // If no specific permission required, just check authentication
  if (!requiredPermission) {
    return children;
  }

  // Check if user has required permission
  if (!hasPermission(user, requiredPermission)) {
    return (
      <MainLayout>
        <div style={{ margin: '24px 16px' }}>
          <Result
            status="403"
            title="403"
            subTitle="Xin lỗi, bạn không có quyền truy cập vào trang này."
          />
        </div>
      </MainLayout>
    );
  }

  return children;
};

// Default dashboard component for users without specific permissions
const DefaultDashboard = () => {
  // Always redirect to home page first
  return <Navigate to="/home" replace />;
};

const AppRoutes = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { token } = useSelector(state => state.auth);
  const isAmzAdsRoute = location.pathname.startsWith('/amz-ads');

  useEffect(() => {
    // Setup store cho API service để handle token expired
    setApiStore(store);
  }, []);

  useEffect(() => {
    // AMZ ADS dùng endpoint options gọn riêng, không tải toàn bộ data-common
    // trong lúc các truy vấn Profit Report đang chạy.
    if (token && !isAmzAdsRoute) {
      dispatch(getDataCommon());
    }
  }, [dispatch, isAmzAdsRoute, token]);

  useEffect(() => {
    if (token) {
      dispatch(fetchUserProfile());
    }
  }, [dispatch, token]);
  
  return (
    <>
      <TokenExpiredDialog />
      <Routes>
      <Route path="/login" element={<Login />} />
      
      <Route path="/" element={
        <ProtectedRoute>
          <MainLayout>
            <DefaultDashboard />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/home" element={
        <ProtectedRoute>
          <MainLayout>
            <Home />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/ads-tool" element={
        <ProtectedRoute requiredPermission="ads.read">
          <MainLayout>
            <AdsOptimization />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/ads-tool/builder/:ruleId" element={
        <ProtectedRoute requiredPermission="workflow.read">
          <MainLayout>
            <FlowBuilder />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/amz-ads" element={
        <ProtectedRoute requiredPermission="profit_report.read">
          <Navigate to="/amz-ads/profit-report" replace />
        </ProtectedRoute>
      } />

      <Route path="/amz-ads/profit-report" element={
        <ProtectedRoute requiredPermission="profit_report.read">
          <AmzAdsLayout>
            <ProfitReport />
          </AmzAdsLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/orders" element={
        <ProtectedRoute requiredPermission="order.read">
          <MainLayout>
            <OrderList />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/orders/:id" element={
        <ProtectedRoute requiredPermission="order.read">
          <MainLayout>
            <OrderDetail />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/financial-transactions" element={
        <ProtectedRoute requiredPermission="financial_adjustment.read">
          <MainLayout>
            <FinancialTransactions />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/shopify/products" element={
        <ProtectedRoute requiredPermission="shopify_product.read">
          <MainLayout>
            <ShopifyProducts />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/shopify/variants" element={
        <ProtectedRoute requiredPermission="shopify_product.read">
          <MainLayout>
            <ShopifyVariants />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/shopify/orders" element={
        <ProtectedRoute requiredPermission="shopify_order.read">
          <MainLayout>
            <ShopifyOrders />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/shopify/settings" element={
        <ProtectedRoute requiredPermission="shopify_store.manage">
          <MainLayout>
            <ShopifySettings />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/inbox" element={
        <ProtectedRoute requiredPermission="order.read">
          <MainLayout>
            <Inbox />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/inventory/sku-items" element={
        <ProtectedRoute requiredPermission="sku_item.read">
          <MainLayout>
            <SkuItems />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/inventory/listings" element={
        <ProtectedRoute requiredPermission="sku_item.read">
          <MainLayout>
            <Listings />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/analytics/niche" element={
        <ProtectedRoute requiredPermission="sku_item.read">
          <MainLayout>
            <NicheAnalysis />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/settings" element={
        <ProtectedRoute requiredPermission="admin_setting.read">
          <MainLayout>
            <AdminSettings />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/system/settings" element={
        <ProtectedRoute requiredPermission="admin_setting.read">
          <MainLayout>
            <SystemSettings />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/system/categories" element={
        <ProtectedRoute requiredPermission="category.read">
          <MainLayout>
            <SystemCategories />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/categories" element={
        <ProtectedRoute requiredPermission="category.read">
          <MainLayout>
            <CategoryManagement />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/shopify/categories" element={
        <ProtectedRoute requiredPermission="shopify_category.read">
          <MainLayout>
            <ShopifyCategoryManagement />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/optimization/sku-template-group" element={
        <ProtectedRoute requiredPermission="design_scale.read">
          <MainLayout>
            <DesignScale />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/optimization/sku-template-group/:groupId" element={
        <ProtectedRoute requiredPermission="design_scale.read">
          <MainLayout>
            <DesignScale />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/optimization/specstudio" element={<ProtectedRoute requiredPermission="specstudio.read"><MainLayout><Specstudio /></MainLayout></ProtectedRoute>} />
      <Route path="/optimization/specstudio/:productId" element={<ProtectedRoute requiredPermission="specstudio.read"><MainLayout><Specstudio /></MainLayout></ProtectedRoute>} />

      <Route path="/designer-bonus" element={
        <ProtectedRoute requiredPermission="designer_bonus.read">
          <MainLayout>
            <DesignerBonus />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute>
          <MainLayout>
            <ProfileSettings />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Catch-all route for unknown paths */}
      <Route path="*" element={
        <ProtectedRoute>
          <MainLayout>
            <div style={{ margin: '24px 16px' }}>
              <Result
                status="404"
                title="404"
                subTitle="Trang bạn tìm kiếm không tồn tại."
              />
            </div>
          </MainLayout>
        </ProtectedRoute>
      } />
      </Routes>
    </>
  );
};

function App() {
  return (
    <Provider store={store}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </Provider>
  );
}

export default App;
